import { useToast } from '@cad-arc';
import { useMutation } from '@tanstack/react-query';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { CadPrintResource, Printable, PrintingDataTypeEnum } from '@mark43/cad-api';
import { useBootstrapState } from '../../../bootstrap/bootstrap-provider';
import { errorOccurredWhile } from '../../../common/helpers/error-occurred-while';
import { getUniversalSearchLabel } from '../../../common/helpers/get-usx-labels';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features';
import { useExportsApi } from '../../../exports/use-exports-api';

const MAX_DEX_ACTIVITY_RESULTS = 4000;

export function useExportDexActivityMutation() {
    const apiClient = useApiClient();
    const bootstrapState = useBootstrapState();
    const toast = useToast();
    const { refetchExports } = useExportsApi();
    const { dexNextEnabled } = useEnabledFeatures();

    return useMutation(({ eventId, eventNumber }: { eventId?: number; eventNumber?: string }) => {
        return apiClient
            .request<CadPrintResource.GetSearchPrintables>({
                method: 'GET',
                url: 'cad_print/search/USER_ACTION/printables',
            })
            .then((resp) => {
                const printables = resp.data.data;
                if (printables) {
                    return printables?.find((printable) => printable.title.includes('DEX'));
                }
            })
            .then((printable?: Printable) => {
                if (printable && eventId && eventNumber) {
                    return apiClient
                        .request<CadPrintResource.StartMillingJobForUserActionSearch>({
                            method: 'POST',
                            url: 'cad_print/user_action',
                            // WorkOrders have minified properties, see BE for full names
                            data: [
                                {
                                    // dataType
                                    dt: PrintingDataTypeEnum.USER_ACTION_SEARCH.name,
                                    // from
                                    f: 0,
                                    // paperFormat
                                    pf: 'PDF',
                                    // printingTemplateId
                                    ptId: printable.printingTemplateId,
                                    // size
                                    s: MAX_DEX_ACTIVITY_RESULTS,
                                    search: {
                                        actionTypesToInclude: [
                                            'USER_MADE_DEX_INQUIRY',
                                            'DEX_HIT_RETURNED_FOR_EVENT',
                                            'DEX_RESULT_RETURNED_FOR_EVENT',
                                        ],
                                        agencyEventIds: [eventId],
                                        ...searchBoilerplateProperties,
                                    },
                                    // title
                                    t: `${getUniversalSearchLabel(dexNextEnabled)} Activity #${eventNumber}`,

                                    // properties to make ts happy
                                    ar: false,
                                    c: [],
                                    cecIds: [],
                                    dId: bootstrapState.cadDepartmentAgencyView.departmentId,
                                    isActingUserSearch: false,
                                    // exportId
                                    // @ts-expect-error don't want to set this
                                    eId: undefined,
                                },
                            ],
                        })
                        .then(() => {
                            refetchExports();
                        });
                } else {
                    toast({
                        status: 'error',
                        description: `Could not export ${getUniversalSearchLabel(
                            dexNextEnabled
                        )} Activity`,
                    });
                }
            })
            .catch(
                createApiErrorHandler(() =>
                    toast({
                        status: 'error',
                        description: errorOccurredWhile(
                            `exporting ${getUniversalSearchLabel(dexNextEnabled)} Activity`
                        ),
                    })
                )
            );
    });
}

const searchBoilerplateProperties = {
    actionTypesToExclude: [],
    departmentIds: [],
    fuzzyMatchingEnabled: false,
    ids: [],
    unitIds: [],
    userIds: [],
    boloIds: [],
    createdDateRangeQuery: {
        startDateUtc: undefined,
        endDateUtc: undefined,
    },
};
